import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FiBook, FiUsers, FiBriefcase, FiShield, FiLayers,
  FiChevronDown, FiChevronRight, FiCheckSquare, FiFolder, FiDollarSign,
  FiSettings, FiUserPlus, FiGrid, FiBarChart2, FiLock, FiFileText
} from 'react-icons/fi';

const DocsSidebar = ({ currentPath }) => {
  const location = useLocation();
  const path = currentPath || location.pathname;

  const navigation = [
    {
      title: 'Getting Started',
      icon: FiBook,
      link: '/docs/getting-started',
      items: [
        { title: 'Create Account', link: '/docs/getting-started#account' },
        { title: 'First Project', link: '/docs/getting-started#first-project' },
        { title: 'Invite Members', link: '/docs/getting-started#invite' },
        { title: 'Workspaces', link: '/docs/getting-started#workspaces' }
      ]
    },
    {
      title: 'Personal Workspace',
      icon: FiUsers,
      link: '/docs/personal',
      items: [
        { title: 'Overview', link: '/docs/personal' },
        { title: 'Task Management', link: '/docs/personal/tasks' },
        { title: 'Project Management', link: '/docs/personal/projects' },
        { title: 'Personal Teams', link: '/docs/personal/teams' },
        { title: 'Personal Finance', link: '/docs/personal/finance' },
        { title: 'Todos', link: '/docs/personal/todos' }
      ]
    },
    {
      title: 'Organization Workspace',
      icon: FiBriefcase,
      link: '/docs/organization',
      items: [
        { title: 'Overview', link: '/docs/organization' },
        { title: 'Organization Setup', link: '/docs/organization#setup' },
        { title: 'Departments', link: '/docs/organization/departments' },
        { title: 'Teams', link: '/docs/organization/teams' },
        { title: 'User Management', link: '/docs/organization/users' },
        { title: 'Finance & Budgets', link: '/docs/organization/finance' },
        { title: 'Analytics', link: '/docs/organization/analytics' },
        { title: 'Billing', link: '/docs/organization/billing' }
      ]
    },
    {
      title: 'Admin & Permissions',
      icon: FiShield,
      link: '/docs/admin',
      items: [
        { title: 'Overview', link: '/docs/admin' },
        { title: 'Role Hierarchy', link: '/docs/admin/roles' },
        { title: 'Permission System', link: '/docs/admin/permissions' },
        { title: 'Organization Admin', link: '/docs/admin/org-admin' },
        { title: 'Security Settings', link: '/docs/admin/security' }
      ]
    },
    {
      title: 'Features',
      icon: FiLayers,
      link: '/docs/features',
      items: [
        { title: 'Overview', link: '/docs/features' },
        { title: 'Views & Boards', link: '/docs/features/views' },
        { title: 'Calendar', link: '/docs/features/calendar' },
        { title: 'Notifications', link: '/docs/features/notifications' },
        { title: 'Analytics', link: '/docs/features/analytics' },
        { title: 'AI Assistance', link: '/docs/features/ai' },
        { title: 'Timeline / Gantt', link: '/docs/features/timeline' },
        { title: 'Task Dependencies', link: '/docs/features/dependencies' }
      ]
    }
  ];

  const isActive = (link) => {
    if (link === path) return true;
    if (link.includes('#') && path.startsWith(link.split('#')[0])) return false;
    return path.startsWith(link) && link !== '/docs';
  };

  const isSectionActive = (section) => {
    if (path.startsWith(section.link)) return true;
    return section.items?.some(item => path === item.link || path.startsWith(item.link.split('#')[0]));
  };

  return (
    <aside className="hidden lg:block w-64 flex-shrink-0 relative">
      <div className="sticky top-0 w-64 h-screen border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
        <div className="p-6 pt-6">
          <Link to="/docs" className="flex items-center space-x-2 mb-6">
            <FiFileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="font-semibold text-gray-900 dark:text-white">Documentation</span>
          </Link>

          <nav className="space-y-1">
            {navigation.map((section, index) => {
              const Icon = section.icon;
              const sectionActive = isSectionActive(section);
              
              return (
                <div key={index} className="mb-4">
                  <Link
                    to={section.link}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sectionActive 
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30' 
                        : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{section.title}</span>
                  </Link>
                  
                  {section.items && sectionActive && (
                    <ul className="mt-1 ml-6 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-3">
                      {section.items.map((item, idx) => (
                        <li key={idx}>
                          <Link
                            to={item.link}
                            className={`block py-1 text-sm transition-colors ${
                              isActive(item.link)
                                ? 'text-blue-600 dark:text-blue-400 font-medium'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                            }`}
                          >
                            {item.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </div>
    </aside>
  );
};

export default DocsSidebar;
